import { GlobalStyles } from '@mui/system'
import { Analytics } from '@core/services/mixpanel'
import { useDrawer } from '@core/context/DrawerContext'
import useAddTechnology from '@/ui/drawer/digitalFootprint/addTechnology/useAddTechnology'
import { generateToast } from '@/ui/components/toasting'
import { ActionButton } from '@/ui/components/buttons/action'
import { AddTechnology } from '@/ui/drawer/digitalFootprint/addTechnology'
import {
    Body,
    Footer,
    Wrapper,
    drawerScrollFixStyles,
} from './AddTechnologiesDrawer.styles'

const AddTechnologiesDrawer = () => {
    const { drawerState, setDrawerState } = useDrawer()
    const {
        selectedTechnologies,
        onRemoveTechnology,
        onSelectTechnology,
        technologiesOptions,
        addTechnologies,
        isLoading,
    } = useAddTechnology()

    const selectedCount = Object.values(selectedTechnologies).length
    const hasSelection = selectedCount > 0

    const selectedCountRef = useRef(selectedCount)

    useEffect(() => {
        selectedCountRef.current = selectedCount
    }, [selectedCount])

    // Keyed on isDrawerOpen, not mount: reopening during the close animation
    // keeps this body mounted, and the provider has already reset onClose.
    useEffect(() => {
        if (!drawerState.isDrawerOpen) return

        setDrawerState((prevState) => ({
            ...prevState,
            onClose: () =>
                Analytics.event('SEM::add_technologies_canceled', {
                    selectedCount: selectedCountRef.current,
                    hadSelection: selectedCountRef.current > 0,
                }),
        }))
    }, [drawerState.isDrawerOpen])

    const closeAfterSuccess = useCallback(() => {
        setDrawerState((prevState) => ({
            ...prevState,
            isDrawerOpen: false,
            drawerContentType: null,
        }))
    }, [setDrawerState])

    const handleOnActionButtonClick = useCallback(async () => {
        if (!hasSelection) {
            return
        }
        const eventProps = { count: selectedCount }
        const isAddTechnologiesSucceeded = await addTechnologies()

        if (isAddTechnologiesSucceeded) {
            Analytics.event('SEM::added_technologies_successfully', eventProps)
            generateToast({
                type: 'success',
                data: `Technologies have been added successfully`,
                testId: 'toast-technologies-added',
            })
            closeAfterSuccess()
        } else {
            Analytics.event('SEM::add_technologies_failed', eventProps)
            generateToast({
                type: 'error',
                data: `Failed to add technologies`,
            })
        }
    }, [hasSelection, selectedCount, addTechnologies, closeAfterSuccess])

    return (
        <Wrapper className="add-technologies-drawer">
            <GlobalStyles styles={drawerScrollFixStyles} />
            <Body>
                <AddTechnology
                    {...{
                        selectedTechnologies,
                        onRemoveTechnology,
                        onSelectTechnology,
                        technologiesOptions,
                        isLoading,
                    }}
                />
            </Body>
            <Footer>
                <ActionButton
                    className="action-button"
                    dataTestId="save-button"
                    loading={isLoading}
                    text="Save"
                    disabled={isLoading || !hasSelection}
                    onClick={handleOnActionButtonClick}
                />
            </Footer>
        </Wrapper>
    )
}

export default AddTechnologiesDrawer
